import React, { useState, useEffect, useMemo } from 'react';
import { ShipmentGroupConfiguration, ShipmentGroupOptions } from '../../../models/shipment-group';
import { useShipmentGroupConfigurations } from '../../../store/hooks/useShipmentGroupConfigurations';
import { TextField, Button, Radio, Checkbox, Tooltip } from '@cimpress/react-components';
import { useInternalShipmentGroups } from '../../../store/hooks/useInternalShipmentGroups';
import { Options, Option } from '../../../models/utils';
import { Select } from '../../Select';
import { valueToArray } from '../../../utils/functions';
import { RadioGroup } from '../RadioGroup';
import FontAwesomeTile from '../../utils/FontAwesomeTile';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { getCarrierAccountOptions, getTagOptions } from './helpers';

// tslint:disable-next-line:import-name
import style from './index.module.css';
import { isUndefined } from 'util';

export type ShipmentGroupConfigurationCreateInput = Omit<ShipmentGroupConfiguration, 'id'>;
export type ShipmentGroupConfigurationUpdateInput = ShipmentGroupConfiguration;

function isShipmentGroupConfigurationUpdateInput(
  input?: ShipmentGroupConfigurationCreateInput | ShipmentGroupConfigurationUpdateInput,
): input is ShipmentGroupConfigurationUpdateInput {
  return !isUndefined(input) && 'id' in input;
}

interface ShipmentGroupConfigurationFormProps {
  close: () => void;
  shipmentGroupConfiguration?: ShipmentGroupConfigurationCreateInput | ShipmentGroupConfigurationUpdateInput;
}

const optionLabels = {
  [ShipmentGroupOptions.ALWAYS_HIDDEN]: 'Always',
  [ShipmentGroupOptions.DEFAULT]: 'When empty',
  [ShipmentGroupOptions.ALWAYS_VISIBLE]: 'Never',
};

// tslint:disable-next-line:function-name
export default function ShipmentGroupConfigurationForm({
  shipmentGroupConfiguration,
  close,
}: ShipmentGroupConfigurationFormProps) {
  const {
    createShipmentGroupConfiguration,
    updateShipmentGroupConfiguration,
  } = useShipmentGroupConfigurations();
  const { internalShipmentGroups } = useInternalShipmentGroups();

  const [name, setName] = useState('');
  const [pinned, setPinned] = useState(false);
  const [manualLoadDisabled, setManualLoadDisabled] = useState(false);
  const [tag, setTag] = useState<Option | undefined>({ label: 'No tag', value: '' });
  const [option, setOption] = useState<ShipmentGroupOptions>(ShipmentGroupOptions.DEFAULT);
  const [carrierAccounts, setCarrierAccounts] = useState<Options>([]);

  const tagOptions = useMemo(
    () => getTagOptions(internalShipmentGroups),
    [internalShipmentGroups],
  );

  const carrierAccountOptions = useMemo(
    () => getCarrierAccountOptions(internalShipmentGroups),
    [internalShipmentGroups],
  );

  const submit = () => {
    const shipmentGroupConfigurationData = {
      option,
      name,
      tag: tag && tag.value || null,
      carrierAccountKeys: carrierAccounts.map(carrierAccount => carrierAccount.value),
      pinned: option !== ShipmentGroupOptions.ALWAYS_HIDDEN && pinned,
      manualLoadDisabled: !!manualLoadDisabled,
    };
    if (isShipmentGroupConfigurationUpdateInput(shipmentGroupConfiguration)) {
      updateShipmentGroupConfiguration({
        id: shipmentGroupConfiguration.id,
        ...shipmentGroupConfigurationData,
      });
    } else {
      createShipmentGroupConfiguration(shipmentGroupConfigurationData);
    }
    close();
  };

  useEffect(
    () => {
      if (option === ShipmentGroupOptions.ALWAYS_HIDDEN) {
        setPinned(false);
      }
    },
    [option],
  );

  useEffect(
    () => {
      if (shipmentGroupConfiguration) {
        setName(shipmentGroupConfiguration.name);
        setTag(tagOptions.find(tag => tag.value === shipmentGroupConfiguration.tag) || tagOptions[0]);
        setCarrierAccounts(
          carrierAccountOptions
            .filter(carrierAccountsKey =>
              shipmentGroupConfiguration.carrierAccountKeys.includes(carrierAccountsKey.value),
            ),
        );
        setOption(shipmentGroupConfiguration.option);
        setPinned(shipmentGroupConfiguration.pinned);
        setManualLoadDisabled(!!shipmentGroupConfiguration.manualLoadDisabled);
      }
    },
    [shipmentGroupConfiguration],
  );

  return <>
    <TextField
      required
      label="Name"
      value={name}
      onChange={e => setName(e.target.value)}
    />
    {
      tagOptions.length > 1 &&
      <Select
        required
        tether
        label="Applies to"
        options={tagOptions}
        value={tag}
        onChange={options => setTag(valueToArray(options)[0])}
      />
    }
    <Select
      required
      tether
      isMulti
      isClearable
      label="Grouped carrier account"
      options={carrierAccountOptions}
      value={carrierAccounts}
      onChange={options => setCarrierAccounts(valueToArray(options))}
    />
    <div className={style.Pinned}>
      <Checkbox
        label={<span>Pin to the top of the page </span>}
        checked={pinned}
        onChange={() => setPinned(!pinned)}
        disabled={option === ShipmentGroupOptions.ALWAYS_HIDDEN}
      />
      <Tooltip
        direction="right"
        contents="Pin the shipment group to the top of list in the shipment page."
      >
        <FontAwesomeTile
          icon={faQuestionCircle}
          color="var(--info)"
          size="1x"
        />
      </Tooltip>
    </div>
    <div className={style.Pinned}>
      <Checkbox
        label={<span>Disable manual load</span>}
        checked={manualLoadDisabled}
        onChange={() => setManualLoadDisabled(!manualLoadDisabled)}
      />
      <Tooltip
        direction="right"
        contents={<>The shipment group will not be displayed in the <i>Load shipments</i> forms.</>}
      >
        <FontAwesomeTile
          icon={faQuestionCircle}
          color="var(--info)"
          size="1x"
        />
      </Tooltip>
    </div>
    <h5 className={style.OptionsLabel}>Hide:</h5>
    <RadioGroup<ShipmentGroupOptions>
      inline
      onChange={(_, value) => setOption(value)}
      valueSelected={option}
      defaultSelected={ShipmentGroupOptions.DEFAULT}
      className={style.Options}
    >
      <Radio label={optionLabels[ShipmentGroupOptions.ALWAYS_HIDDEN]} value={ShipmentGroupOptions.ALWAYS_HIDDEN} />
      <Radio label={optionLabels[ShipmentGroupOptions.DEFAULT]} value={ShipmentGroupOptions.DEFAULT} />
      <Radio label={optionLabels[ShipmentGroupOptions.ALWAYS_VISIBLE]} value={ShipmentGroupOptions.ALWAYS_VISIBLE} />
    </RadioGroup>
    <div className={`d--f ${style.Actions}`}>
      <span className="fg--1" />
      <Button onClick={close} style={{ marginRight: '5px' }}>
        Cancel
      </Button>
      <Button type="primary" onClick={submit} disabled={!name || !carrierAccounts.length}>
        Save
      </Button>
    </div>
  </>;
}
